import { Controller } from "@hotwired/stimulus"
import { MapFactory } from "./map/map_factory.js"
import { MapRenderer } from "./map/map_renderer.js"
import { DataFetcher } from "./map/data_fetcher.js"
import { MapUtils } from "./map/map_utils.js"
import { HuntingAreasRenderer } from "./map/hunting_areas_renderer.js"
import { KilledGameRenderer } from "./map/killed_game_renderer.js"

export default class extends Controller {
  static targets = ["container"]
  static values = { 
    amenityUrl: String, 
    personalUrl: String, 
    seenUrl: String,
    huntingAreasUrl: String,
    killedGameUrl: String,
    layerType: String,
    killedGame: Array
  }

  connect() {
    this.initializeComponents();
    
    setTimeout(() => {
      if (this.element.dataset.geolocation) {
        this.drawMainMap();
      } else if (this.element.dataset.location) {
        this.previewLocation();
      }
    }, 500); // Delay to ensure the map container is ready
  }

  initializeComponents() {
    this.renderer = null;
    this.dataFetcher = null;
    this.debouncedLoad = null;
    this.huntingAreasRenderer = null;
    this.killedGameRenderer = null;
  }

  previewLocation() {
    const center = JSON.parse(this.element.dataset.location);
    this.map = MapFactory.createPreviewMap(this.containerTarget, center);
    
    this.renderer = new MapRenderer(this.map);
    this.renderer.renderPreviewLocation(center);
  }

  drawMainMap() {
    const center = JSON.parse(this.element.dataset.geolocation);
    const options = {
      wheelZoom: this.element.dataset.wheelZoom,
      layerType: this.layerTypeValue
    };    
    
    this.map = MapFactory.createMainMap(this.containerTarget, center, options);
    
    // Initialize components
    this.renderer = new MapRenderer(this.map);
    this.dataFetcher = new DataFetcher(this.map);
    this.huntingAreasRenderer = new HuntingAreasRenderer(this.map, this.dataFetcher);
    this.killedGameRenderer = new KilledGameRenderer(this.map);
    
    // Render killed game if data is provided
    if (this.hasKilledGameValue && this.killedGameValue.length > 0) {
      this.killedGameRenderer.render(this.killedGameValue);
    }
    
    // Setup data fetching if URLs are provided
    this.setupDataFetching();
  }

  setupDataFetching() {
    if (this.amenityUrlValue) {
      this.debouncedLoad = MapUtils.debounce(this.drawHuntingData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoad);
      this.drawHuntingData();
    }

    if (this.personalUrlValue) {
      this.debouncedLoadPersonal = MapUtils.debounce(this.drawPersonalData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadPersonal);
      this.drawPersonalData();
    }

    if (this.seenUrlValue) {
      this.debouncedLoadSeen = MapUtils.debounce(this.drawSeenData.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadSeen);
      this.drawSeenData();
    }

    if (this.huntingAreasUrlValue) {
      this.debouncedLoadHuntingAreas = MapUtils.debounce(this.drawHuntingAreas.bind(this), 500);
      this.map.on("moveend", this.debouncedLoadHuntingAreas);
      this.drawHuntingAreas();
    }

    // Setup dropdown click handler
    this.setupDropdownHandler();
  }

  // Toggle hunting areas layer visibility
  toggleHuntingAreas(event) {
    const isChecked = event.target.checked;
    
    if (this.huntingAreasRenderer) {
      if (isChecked) {
        this.huntingAreasRenderer.show();
      } else {
        this.huntingAreasRenderer.hide();
      }
    }

    // Save the state to session
    this.saveHuntingAreasToggleState(isChecked);
  }

  // Save hunting areas toggle state to session
  async saveHuntingAreasToggleState(visible) {
    try {
      await fetch('/map/toggle_hunting_areas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ visible: visible })
      });
    } catch (error) {
      console.error('Failed to save hunting areas toggle state:', error);
    }
  }

  // Toggle killed game layer visibility
  toggleKilledGame(event) {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      // Load and show data
      this.drawKilledGame();
    } else {
      // Hide data
      if (this.killedGameRenderer) {
        this.killedGameRenderer.hide();
      }
    }
  }

  async drawKilledGame() {
    if (this.killedGameRenderer && this.killedGameUrlValue) {
      try {
        const response = await fetch(this.killedGameUrlValue);
        const data = await response.json();
        
        if (data && data.length > 0) {
          this.killedGameRenderer.render(data);
          this.killedGameRenderer.show();
        }
      } catch (error) {
        console.error('Failed to fetch killed game:', error);
      }
    }
  }

  async drawHuntingData() {
    const data = await this.dataFetcher.fetchHuntingData(this.amenityUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawPersonalData() {
    const data = await this.dataFetcher.fetchPersonalData(this.personalUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawSeenData() {
    const data = await this.dataFetcher.fetchSeenData(this.seenUrlValue);
    if (data) {
      this.renderer.renderData(data);
    }
  }

  async drawHuntingAreas() {
    if (this.huntingAreasRenderer) {
      await this.huntingAreasRenderer.loadAndRender(this.huntingAreasUrlValue);
      
      // Check the checkbox state and hide/show accordingly
      const checkbox = document.getElementById('huntingAreasToggle');
      if (checkbox && !checkbox.checked) {
        this.huntingAreasRenderer.hide();
      }
    }
  }

  // Setup dropdown click handler
  setupDropdownHandler() {
    setTimeout(() => {
      const dropdown = document.getElementById('mapAddDropdown');
      if (dropdown) { dropdown.addEventListener('click', this.updateDropdownLinks.bind(this)); }
    }, 500);
  }

  // Get current map center coordinates
  getCurrentMapCenter() {
    if (this.map) {
      const center = this.map.getCenter();
      return {
        latitude: center.lat,
        longitude: center.lng
      };
    }
    return null;
  }

  // Update dropdown links with current map center
  updateDropdownLinks() {
    const center = this.getCurrentMapCenter();
    if (!center) return;

    const dropdown = document.getElementById('mapAddDropdown');
    if (!dropdown) return;

    const dropdownMenu = dropdown.nextElementSibling;
    const links = dropdownMenu.querySelectorAll('a.dropdown-item');

    links.forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('latitude', center.latitude);
      url.searchParams.set('longitude', center.longitude);
      link.href = url.toString();
    });
  }
}
